import React, { useState } from 'react';
import { debounce } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import FormAutocomplete from 'components/FormFields/FormAutocomplete';
import { useGooglePlacesAutocomplete } from 'hooks/useGooglePlacesAutocomplete';
import { parseGooglePlace } from 'utils/google-autocomplete';

import './location-select.scss';

const LocationSelect = (props) => {
  const {
    location,
    setLocation,
    name = 'Location',
    ...rest
  } = props;
  const { formattedAddress = '' } = location || {};
  const { sessionToken, autocompleteService, placesService } = useGooglePlacesAutocomplete();
  const [options, setOptions] = useState([]);

  const handleResults = (predictions, status) => {
    /* eslint-disable-next-line no-undef */
    if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) return;
    setOptions(predictions);
  };

  const handlePlacesResult = (result, status) => {
    /* eslint-disable-next-line no-undef */
    if (status !== google.maps.places.PlacesServiceStatus.OK || !result) return;
    const place = parseGooglePlace(result);
    setLocation(place);
  };

  if (!autocompleteService) return <></>;

  const search = (input) => {
    // Service takes arguments and callback. Callback handles the results from the search string.
    autocompleteService.getPlacePredictions({
      input,
      type: ['geocode'],
      sessionToken,
    }, handleResults);
  };

  // Using a debounce so that we're not calling the autocomplete service on every change.
  const debouncedSearch = debounce(search, 500);

  const handleInputChange = (e) => {
    debouncedSearch(e.target.value);
  };

  const handleSubmit = (value) => {
    // When the input blurs, we don't want to call the state setter if it matches the current location set.
    if (value && value !== formattedAddress) {
      const newLocation = { formattedAddress: value };
      const split = value.split(',');
      const [city, state, country] = split || [];
      // We are going to assume if the split has length 1, it's just a label and not fill any place data
      // or if length is 2, it is city, state
      // or if length is 3, it is city, state, country
      const count = split.length;
      switch (count) {
        // Just city
        case 3:
          newLocation.country = country;
          // Falls through.
        case 2:
          newLocation.administrative_area_level_1 = state;
          newLocation.city = city;
          break;
        default:
          break;
      }
      setLocation(newLocation);
    } else if (!value || value === '') {
      // We want to be able to delete locations too.
      setLocation(null);
    }
  };

  const handleOptionSelect = (option) => {
    const { place_id } = option;
    // getDetails takes the place id and a callback. The callback handles the details result.
    // #TODO: I think the session token should be passed here to optimize map api billing.
    placesService.getDetails({ placeId: place_id }, handlePlacesResult);
  };

  const renderOption = (item, i) => {
    const { structured_formatting } = item;
    const { main_text, secondary_text } = structured_formatting || {};
    return (
      <button
        key={`location-autocomplete-option-${i}`}
        className="button--unstyled location-autocomplete__result autocomplete__option"
        onMouseDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        onClick={() => handleOptionSelect(item)}
      >
        <div className="location-autocomplete__result-icon">
          <FontAwesomeIcon icon={['fa', 'map-marker-alt']}/>
        </div>
        <div className="location-autocomplete__data">
          <div className="location-autocomplete__main-text">
            {main_text}
          </div>
          <div className="location-autocomplete__secondary-text">
            {secondary_text}
          </div>
        </div>
      </button>
    );
  };

  return (
    <>
      <FormAutocomplete
        id="location-autocomplete"
        name={name}
        options={options}
        value={formattedAddress}
        onSubmit={handleSubmit}
        onInputChange={handleInputChange}
        renderOption={renderOption}
        submitOnBlur
        {...rest}
      />
    </>
  );
};

export default LocationSelect;
